import React, { useState, useEffect } from "react";
import { BalanceCheckResult } from "../types/validation";
import { useWalletBalance } from "../hooks/useValidation";
import "./BalanceChecker.css";

interface BalanceCheckerProps {
  tradeId?: number;
  quantity: number;
  price: number;
  balanceResult?: BalanceCheckResult | null;
  onBalanceCheck?: (result: BalanceCheckResult) => void;
  onDeposit?: (amount: number) => Promise<void> | void;
  className?: string;
}

const BalanceChecker: React.FC<BalanceCheckerProps> = ({
  tradeId,
  quantity,
  price,
  balanceResult: propBalanceResult,
  onBalanceCheck,
  onDeposit,
  className = "",
}) => {
  // Use the custom hook for balance checking if no propBalanceResult is provided
  const {
    balanceResult: hookBalanceResult,
    isLoading,
    checkBalance,
    refreshBalance,
  } = useWalletBalance(
    propBalanceResult ? 0 : (tradeId ?? 0),
    propBalanceResult ? 0 : quantity,
    propBalanceResult ? 0 : price,
  );

  // Use prop balance result if provided, otherwise use hook result
  const balanceResult = propBalanceResult || hookBalanceResult;
  const [error, setError] = useState<string | null>(null);
  const [depositError, setDepositError] = useState<string | null>(null);

  // Use balanceResult.requiredAmount if available, otherwise calculate from props
  const requiredAmount = balanceResult?.requiredAmount || quantity * price;

  // Update local state when prop changes
  useEffect(() => {
    if (propBalanceResult) {
      setError(null); // Clear any previous errors
    }
  }, [propBalanceResult]);

  // Call onBalanceCheck when hook result changes
  useEffect(() => {
    if (hookBalanceResult && onBalanceCheck) {
      onBalanceCheck(hookBalanceResult);
    }
  }, [hookBalanceResult, onBalanceCheck]);

  // Handle errors from the hook
  useEffect(() => {
    if (
      hookBalanceResult === null &&
      !propBalanceResult &&
      tradeId &&
      quantity > 0 &&
      price > 0
    ) {
      setError("Failed to check balance");
    } else {
      setError(null);
    }
  }, [hookBalanceResult, propBalanceResult, tradeId, quantity, price]);

  const handleDeposit = async () => {
    if (!balanceResult || !onDeposit) return;

    setDepositError(null);
    try {
      await onDeposit(balanceResult.suggestedDeposit);
      await refreshBalance();
    } catch (err: any) {
      const message =
        err?.graphQLErrors?.[0]?.message || err?.message || "Deposit failed";
      setDepositError(message);
    }
  };

  if (isLoading) {
    return (
      <div className={`balance-checker loading ${className}`}>
        <div className="balance-loading">Checking balance...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`balance-checker error ${className}`}>
        <div className="balance-error">Error: {error}</div>
        <button onClick={refreshBalance} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (!balanceResult) {
    return null;
  }

  return (
    <div className={`balance-checker ${className}`}>
      <div className="balance-header">
        <span className="balance-icon">💰</span>
        <span className="balance-title">Wallet Balance Check</span>
      </div>

      <div className="balance-details">
        <div className="balance-row">
          <span className="balance-label">Required Amount:</span>
          <span className="balance-value required">
            ${requiredAmount.toLocaleString()}
          </span>
        </div>

        <div className="balance-row">
          <span className="balance-label">Current Balance:</span>
          <span className="balance-value current">
            ${balanceResult.currentBalance.toLocaleString()}
          </span>
        </div>

        {!balanceResult.hasSufficientBalance && (
          <>
            <div className="balance-row">
              <span className="balance-label">Shortfall:</span>
              <span className="balance-value shortfall">
                ${balanceResult.shortfall.toLocaleString()}
              </span>
            </div>

            <div className="balance-row">
              <span className="balance-label">Suggested Deposit:</span>
              <span className="balance-value suggested">
                ${balanceResult.suggestedDeposit.toLocaleString()}
              </span>
            </div>
          </>
        )}
      </div>

      {!balanceResult.hasSufficientBalance && onDeposit && (
        <div className="balance-actions">
          <button onClick={handleDeposit} className="deposit-button primary">
            Deposit ${balanceResult.suggestedDeposit.toLocaleString()}
          </button>
          <div className="deposit-note">
            This includes a 10% buffer for fees and market fluctuations
          </div>
          {depositError && <div className="deposit-error">{depositError}</div>}
        </div>
      )}

      {balanceResult.hasSufficientBalance && (
        <div className="balance-success">
          <span className="success-icon">✅</span>
          <span className="success-text">Sufficient funds available</span>
        </div>
      )}
    </div>
  );
};

export default BalanceChecker;
